import { useEffect, useState } from 'react';
import axios from '../api/axios';
import { useAuth } from '../context/AuthContext';

const ORANGE = '#f57c00';
const LIGHT_BG = '#fff8f2';

const formatDate = (dateStr) => {
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric'
  });
};

const formatTime = (timeStr) => {
  if (!timeStr) return '';
  const [h, m] = timeStr.split(':');
  const date = new Date();
  date.setHours(h, m);
  return date.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });
};

const getShiftCategory = (shiftDateStr) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const shiftDate = new Date(shiftDateStr);
  shiftDate.setHours(0, 0, 0, 0);
  if (shiftDate.getTime() === today.getTime()) return 'today';
  if (shiftDate > today) return 'upcoming';
  return 'completed';
};

const Badge = ({ label, color, bg }) => (
  <span style={{
    background: bg,
    color: color,
    padding: '3px 12px',
    borderRadius: 20,
    fontSize: 12,
    fontWeight: 600,
    letterSpacing: 0.3
  }}>
    {label}
  </span>
);

const ShiftCard = ({ s }) => {
  const category = getShiftCategory(s.shift_date);

  const badgeMap = {
    today:     { label: '📍 Today',     color: '#1565c0', bg: '#e3f2fd' },
    upcoming:  { label: '🕐 Upcoming',  color: '#6a1b9a', bg: '#f3e5f5' },
    completed: { label: '✅ Completed', color: '#2e7d32', bg: '#e8f5e9' },
  };

  const presenceBadge = s.is_present
    ? { label: 'Present', color: '#2e7d32', bg: '#e8f5e9' }
    : { label: 'Assigned', color: '#e65100', bg: '#fff3e0' };

  return (
    <div style={{
      background: 'white',
      padding: '16px 20px',
      borderRadius: 12,
      borderLeft: `4px solid ${ORANGE}`,
      boxShadow: '0 1px 6px rgba(0,0,0,0.06)',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      gap: 10
    }}>
      <div style={{ flex: 1 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
          <span style={{ fontWeight: 700, fontSize: 16, color: '#222' }}>{s.shift_name}</span>
          <Badge {...badgeMap[category]} />
          <Badge {...presenceBadge} />
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 20px', fontSize: 13, color: '#555' }}>
          <span>📋 <b>{s.exam_name}</b></span>
          <span>🏷 {s.exam_type}</span>
          <span>📅 {formatDate(s.shift_date)}</span>
          <span>⏰ {formatTime(s.start_time)} – {formatTime(s.end_time)}</span>
        </div>
      </div>
    </div>
  );
};

const SectionHeader = ({ title, count }) => (
  <div style={{
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    margin: '28px 0 12px'
  }}>
    <h3 style={{ color: ORANGE, margin: 0 }}>{title}</h3>
    <span style={{
      background: ORANGE,
      color: 'white',
      borderRadius: 20,
      padding: '1px 10px',
      fontSize: 12,
      fontWeight: 700
    }}>{count}</span>
  </div>
);

const InvigilatorDashboard = () => {
  const { user } = useAuth();

  const [shifts, setShifts]             = useState([]);
  const [remuneration, setRemuneration] = useState(0);
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState(null);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [shiftRes, remRes] = await Promise.all([
        axios.get('/shifts/my'),
        axios.get('/remuneration/my')
      ]);
      setShifts(shiftRes.data || []);
      setRemuneration(remRes.data?.total ?? 0);
    } catch (err) {
      console.error('Dashboard error:', err.response?.status, err.response?.data);
      setError('Failed to load dashboard data.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  if (loading) return (
    <div style={{ padding: 40, textAlign: 'center', color: '#888' }}>Loading...</div>
  );

  if (error) return (
    <div style={{ padding: 30, color: 'red' }}>
      {error}
      <button onClick={fetchData} style={{
        marginLeft: 10, cursor: 'pointer',
        padding: '4px 12px', borderRadius: 6,
        border: '1px solid red', background: 'white', color: 'red'
      }}>Retry</button>
    </div>
  );

  const todayShifts     = shifts.filter(s => getShiftCategory(s.shift_date) === 'today');
  const upcomingShifts  = shifts.filter(s => getShiftCategory(s.shift_date) === 'upcoming');
  const completedShifts = shifts.filter(s => getShiftCategory(s.shift_date) === 'completed');

  return (
    <div style={{ padding: 30, background: LIGHT_BG, minHeight: '100vh' }}>

      <h2 style={{ color: ORANGE, marginBottom: 20 }}>Welcome, {user?.name}</h2>

      {/* ── Summary Cards ── */}
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>

        <div style={card}>
          <div style={cardLabel}>Total Shifts</div>
          <div style={cardValue}>{shifts.length}</div>
        </div>

        <div style={card}>
          <div style={cardLabel}>Today</div>
          <div style={cardValue}>{todayShifts.length}</div>
        </div>

        <div style={card}>
          <div style={cardLabel}>Upcoming</div>
          <div style={cardValue}>{upcomingShifts.length}</div>
        </div>

        <div style={card}>
          <div style={cardLabel}>Completed</div>
          <div style={cardValue}>{completedShifts.length}</div>
        </div>

        <div style={{ ...card, borderLeft: '5px solid #2e7d32', flex: 2, minWidth: 180 }}>
          <div style={cardLabel}>My Remuneration</div>
          <div style={{ ...cardValue, color: '#2e7d32' }}>
            ₹{Number(remuneration).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
          </div>
        </div>

      </div>

      {/* ── Today's Shifts ── */}
      {todayShifts.length > 0 && (
        <>
          <SectionHeader title="📍 Today's Shifts" count={todayShifts.length} />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {todayShifts.map(s => <ShiftCard key={s.id} s={s} />)}
          </div>
        </>
      )}

      {/* ── Upcoming Shifts ── */}
      {upcomingShifts.length > 0 && (
        <>
          <SectionHeader title="🕐 Upcoming Shifts" count={upcomingShifts.length} />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {upcomingShifts.map(s => <ShiftCard key={s.id} s={s} />)}
          </div>
        </>
      )}

      {/* ── Completed Shifts ── */}
      {completedShifts.length > 0 && (
        <>
          <SectionHeader title="✅ Completed Shifts" count={completedShifts.length} />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {completedShifts.map(s => <ShiftCard key={s.id} s={s} />)}
          </div>
        </>
      )}

      {/* ── No shifts at all ── */}
      {shifts.length === 0 && (
        <p style={{ color: '#999', marginTop: 30 }}>No shifts assigned yet.</p>
      )}

    </div>
  );
};

const card = {
  background: 'white',
  padding: '16px 20px',
  borderRadius: 12,
  flex: 1,
  minWidth: 120,
  borderLeft: '5px solid #f57c00',
  boxShadow: '0 1px 6px rgba(0,0,0,0.06)'
};

const cardLabel = {
  fontSize: 13,
  color: '#888',
  marginBottom: 6,
  fontWeight: 500
};

const cardValue = {
  fontSize: 28,
  fontWeight: 700,
  color: ORANGE
};

export default InvigilatorDashboard;